import React from "react";
import PropTypes from "prop-types";
import PnLStatusChip from "./PnLStatusChip";
import { formatMoney, formatNetMargin, getCategoryLabel, getStatusInfo, monthLabel, netClass } from "./pnlReportUtils";

const rowKey = (row) => `${row.category}|${row.subType}|${row.companySellerId ?? "all"}`;

const NetAmount = ({ value }) => {
  if (!Number(value)) return <span className="pnl-dim">{formatMoney(0)}</span>;
  return <span className={netClass(value) ? "pnl-neg" : undefined}>{formatMoney(value)}</span>;
};

NetAmount.propTypes = { value: PropTypes.number };

const NetMargin = ({ net, gross }) => {
  const margin = formatNetMargin(net, gross);
  return margin ? <span className="pnl-margin">{margin}</span> : null;
};

NetMargin.propTypes = { net: PropTypes.number, gross: PropTypes.number };

const ItemRows = ({ group, onSelectRow }) => {
  let previousCategory = null;
  return group.rows.map((row, index) => {
    const startsCategory = row.category !== previousCategory;
    previousCategory = row.category;
    return (
      <tr key={rowKey(row)} className={index > 0 && startsCategory ? "pnl-category-start" : undefined}>
        <td className="pnl-category">{startsCategory ? getCategoryLabel(row.category) : ""}</td>
        <td>
          <button
            type="button"
            className="pnl-row-link"
            onClick={() => onSelectRow(row)}
            aria-label={`View details for ${getCategoryLabel(row.category)} ${row.subType}, ${monthLabel(row.year, row.month)}`}
          >
            {row.subType}
          </button>
        </td>
        <td className="pnl-num">{formatMoney(row.grossAmount)}</td>
        <td className="pnl-num">
          <NetAmount value={row.netAmount} />
          <NetMargin net={row.netAmount} gross={row.grossAmount} />
        </td>
        <td><PnLStatusChip status={getStatusInfo(row)} /></td>
      </tr>
    );
  });
};

ItemRows.propTypes = {
  group: PropTypes.shape({ rows: PropTypes.array.isRequired }).isRequired,
  onSelectRow: PropTypes.func.isRequired,
};

const MonthGroup = ({ group, collapsed, onToggle, onSelectRow }) => {
  const label = monthLabel(group.year, group.month);
  return (
    <>
      <tr className="pnl-month">
        <td colSpan={2}>
          <button
            type="button"
            className="pnl-month-toggle"
            aria-expanded={!collapsed}
            onClick={() => onToggle(group.key)}
          >
            <span className="pnl-caret" aria-hidden="true">▾</span>
            {label}
          </button>
        </td>
        <td className="pnl-num">{formatMoney(group.totals.gross)}</td>
        <td className="pnl-num">
          <strong>{formatMoney(group.totals.net)}</strong>
          <NetMargin net={group.totals.net} gross={group.totals.gross} />
        </td>
        <td><PnLStatusChip status={group.status} /></td>
      </tr>
      {!collapsed && (
        <>
          <ItemRows group={group} onSelectRow={onSelectRow} />
          <tr className="pnl-subtotal">
            <td colSpan={2}>{label} total</td>
            <td className="pnl-num">{formatMoney(group.totals.gross)}</td>
            <td className="pnl-num">
              {formatMoney(group.totals.net)}
              <NetMargin net={group.totals.net} gross={group.totals.gross} />
            </td>
            <td />
          </tr>
        </>
      )}
    </>
  );
};

MonthGroup.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    year: PropTypes.number.isRequired,
    month: PropTypes.number.isRequired,
    totals: PropTypes.object.isRequired,
    status: PropTypes.object.isRequired,
  }).isRequired,
  collapsed: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
};

const PnLTable = ({ groups, totals, collapsedKeys, onToggleMonth, onSelectRow }) => (
  <div className="pnl-table-scroll">
    <table className="pnl-table">
      <thead>
        <tr>
          <th style={{ width: 150 }}>Month / Category</th>
          <th>Type</th>
          <th className="pnl-num">Gross amount</th>
          <th className="pnl-num">Net amount</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <MonthGroup
            key={group.key}
            group={group}
            collapsed={collapsedKeys.has(group.key)}
            onToggle={onToggleMonth}
            onSelectRow={onSelectRow}
          />
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2}>Grand total</td>
          <td className="pnl-num">{formatMoney(totals.gross)}</td>
          <td className={`pnl-num ${totals.net < 0 ? "pnl-neg" : ""}`}>{formatMoney(totals.net)}</td>
          <td />
        </tr>
      </tfoot>
    </table>
  </div>
);

PnLTable.propTypes = {
  groups: PropTypes.array.isRequired,
  totals: PropTypes.shape({ gross: PropTypes.number, net: PropTypes.number }).isRequired,
  collapsedKeys: PropTypes.instanceOf(Set).isRequired,
  onToggleMonth: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
};

export default PnLTable;
